// OidcCallback.tsx
import { useEffect, useContext } from "react";
import { useNavigate } from "react-router";
import { Log } from "oidc-client-ts";
import { AuthContext } from "../Context/AuthContext";
import httpService from "../services/httpService";
import { jwtDecode } from "jwt-decode";
import { oidcUserManager } from "../auth/userManager";

Log.setLogger(console);
Log.setLevel(Log.DEBUG);

type ValidateUserResponse = {
  roles?: string[];
  role?: string | string[];
  Roles?: string[];
  Role?: string | string[];
  [k: string]: any;
};

type AuthResponse = {
  Token?: string;
  token?: string;
};

type DecodedExp = {
  exp?: number;
  [k: string]: any;
};

const OidcCallback = () => {
  const navigate = useNavigate();

  const { setIsAuthenticated, setUser, setIsUserValidated } =
    useContext(AuthContext);

  useEffect(() => {
    const handleOidcRedirect = async () => {
      console.debug("[OidcCallback] Starting OIDC redirect handling");
      console.log("[OidcCallback] Starting OIDC redirect handling");

      const toArray = (r: unknown): string[] =>
        Array.isArray(r) ? r.filter(Boolean) : r ? [String(r)] : [];

      const decodeRolesFromJwt = (jwt?: string): string[] => {
        if (!jwt) return [];

        try {
          const part = jwt.split(".")[1] ?? "";
          const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
          const pad = b64 + "===".slice((b64.length + 3) % 4);
          const payload = JSON.parse(atob(pad));

          const toArrayInner = (r: unknown): string[] =>
            Array.isArray(r) ? r.filter(Boolean) : r ? [String(r)] : [];

          return toArrayInner(payload?.role ?? payload?.roles ?? []);
        } catch {
          return [];
        }
      };

      const readExp = (token?: string | null): number | null => {
        if (!token) return null;

        try {
          const decoded = jwtDecode<DecodedExp>(token);
          return decoded?.exp ?? null;
        } catch {
          return null;
        }
      };

      const params = new URLSearchParams(window.location.search);
      const error = params.get("error");

      if (error === "access_denied") {
        console.warn(
          "[OidcCallback] SiteMinder returned access_denied, stopping login"
        );

        sessionStorage.setItem("loginDenied", "true");
        navigate("/login-error");
        return;
      }

      try {
        const user = await oidcUserManager.signinRedirectCallback();

        if (!user?.access_token || !user?.id_token) {
          console.error("[OidcCallback] OIDC login failed, missing tokens");
          navigate("/error");
          return;
        }

        const accessExp = readExp(user.access_token);
        const idExp = readExp(user.id_token);

        console.log("[OidcCallback] Tokens received from SiteMinder");
        console.log("[OidcCallback] User received", user);

        console.log(
          "[OidcCallback] Refresh token present?",
          !!user.refresh_token
        );

        console.log(
          "[OidcCallback] Refresh token preview:",
          user.refresh_token
            ? `${user.refresh_token.substring(0, 25)}...`
            : "(none)"
        );

        console.log("[OidcCallback] Access token exp:", accessExp);

        console.log(
          "[OidcCallback] Access token exp time:",
          accessExp
            ? new Date(accessExp * 1000).toLocaleString()
            : "(none)"
        );

        console.log("[OidcCallback] ID token exp:", idExp);

        console.log(
          "[OidcCallback] ID token exp time:",
          idExp ? new Date(idExp * 1000).toLocaleString() : "(none)"
        );

        console.log("[OidcCallback] Granted scope:", user.scope);

        sessionStorage.removeItem("authorizationToken");

        sessionStorage.setItem("access_token", user.access_token);
        sessionStorage.setItem("id_token", user.id_token);
        sessionStorage.setItem("authenticated", "true");
        sessionStorage.setItem("authProvider", "siteminder");

        if (user.refresh_token) {
          sessionStorage.setItem("has_refresh_token", "true");
        } else {
          sessionStorage.removeItem("has_refresh_token");
        }

        if (accessExp) {
          sessionStorage.setItem("accessTokenExp", String(accessExp));
        }

        const claims = user.profile;

        console.log("[OidcCallback] OIDC Claims:", claims);

        setIsAuthenticated(true);
        setUser(user);
        setIsUserValidated(false);

        try {
          console.log(
            "[OidcCallback] Calling backend to validate or create user"
          );

          const response = await httpService.post<ValidateUserResponse>(
            "/User/ValidateOrCreateUser",
            {
              email: claims.email,
              role: claims.role,
              name: claims.name || claims.preferred_username,
            }
          );

          console.log(
            "[OidcCallback] User validated or created",
            response.data
          );

          const data = response?.data ?? {};

          const apiRoles = Array.from(
            new Set(
              toArray(
                data.user?.roles ??
                  data.roles ??
                  data.Roles ??
                  data.role ??
                  data.Role
              )
            )
          );

          const appUserForUi = {
            name: user.profile?.name ?? "",
            email: user.profile?.email ?? "",
            groups: apiRoles,
          };

          sessionStorage.setItem(
            "appUser",
            JSON.stringify(appUserForUi)
          );

          setUser(user);
          setIsUserValidated(true);
        } catch (apiErr) {
          console.error(
            "[OidcCallback] Error validating user in backend",
            apiErr
          );

          navigate("/error");
          return;
        }

        try {
          console.log(
            "[OidcCallback] Fetching backend authorization token from API"
          );

          const resp =
            await httpService.get<AuthResponse>(
              "/User/GetAuthorizationToken"
            );

          const backendToken = resp.data.Token ?? resp.data.token;

          if (backendToken) {
            sessionStorage.setItem(
              "authorizationToken",
              backendToken
            );

            console.log(
              "[OidcCallback] Stored authorizationToken in sessionStorage"
            );

            const tokenRoles = decodeRolesFromJwt(backendToken);

            const currentRaw =
              sessionStorage.getItem("appUser");

            let current: {
              name: string;
              email: string;
              groups: string[];
            } = {
              name: "",
              email: "",
              groups: [],
            };

            try {
              if (currentRaw) {
                current = JSON.parse(currentRaw);
              }
            } catch {
              // ignore bad JSON and keep default
            }

            const mergedRoles = Array.from(
              new Set([
                ...(current.groups ?? []),
                ...tokenRoles,
              ])
            );

            const updatedAppUser = {
              ...current,
              groups: mergedRoles,
            };

            sessionStorage.setItem(
              "appUser",
              JSON.stringify(updatedAppUser)
            );

            console.log(
              "[OidcCallback] Merged roles from API and backend token",
              mergedRoles
            );
          } else {
            console.warn(
              "[OidcCallback] GetAuthorizationToken response missing token field",
              resp.data
            );
          }
        } catch (e) {
          console.error(
            "[OidcCallback] Failed to fetch backend authorization token",
            e
          );
        }

        sessionStorage.removeItem("loginDenied");
        navigate("/pages/home");
      } catch (err) {
        console.error(
          "[OidcCallback] OIDC callback error",
          err
        );

        navigate("/error");
      }
    };

    handleOidcRedirect();
  }, [
    navigate,
    setIsAuthenticated,
    setUser,
    setIsUserValidated,
  ]);

  return <div>Processing login...</div>;
};

export default OidcCallback;